// src/pages/admin/capacity/ProjectModal.jsx
import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  DatePicker,
  Switch,
  Space,
  Select,
  message,
  Card,
  Flex,
  Divider,
  Button,
  Upload,
  Progress,
} from "antd";
import axios from "../../../requests/axios";
import routes from "../../../requests/routes";
import RichText from "../RichTextEditor/RichTextEditor";
import dayjs from "dayjs";
import {
  PlusOutlined,
  DeleteOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { uploadOne } from "../../../utils/utils"; // must resolve to URL and support onProgress

const rawFile = (f) => (f?.originFileObj ? f.originFileObj : f);

export default function ProjectModal({ open, project, onClose, onSaved }) {
  const [form] = Form.useForm();

  // Upload fileLists (max 1 each)
  const [logoFiles, setLogoFiles] = useState([]);
  const [bannerFiles, setBannerFiles] = useState([]);
  const [programLogoFiles, setProgramLogoFiles] = useState([]);

  // Preview modal
  const [preview, setPreview] = useState({ open: false, src: "", title: "" });

  // Save & progress
  const [saving, setSaving] = useState(false);
  const [logoPct, setLogoPct] = useState(0);
  const [bannerPct, setBannerPct] = useState(0);
  const [programLogoPct, setProgramLogoPct] = useState(0);

  // seed form + previews on open
  useEffect(() => {
    if (!open) return;

    if (project) {
      form.setFieldsValue({
        name: project.name || "",
        slug: project.slug || "",
        programName: project.programName || "",
        type: project.type || "",
        referenceNumber: project.referenceNumber || "",
        startDate: project.startDate ? dayjs(project.startDate) : null,
        endDate: project.endDate ? dayjs(project.endDate) : null,
        logo: project.logo || "",
        bannerImage: project.bannerImage || "",
        overviewHtml: project.overviewHtml || "",
        program_logo: project?.program?.logo || "",
        program_name: project?.program?.name || "",
        program_description: project?.program?.description || "",
        website: project?.socials?.website || "",
        facebook: project?.socials?.facebook || "",
        instagram: project?.socials?.instagram || "",
        x: project?.socials?.x || "",
        linkedin: project?.socials?.linkedin || "",
        youtube: project?.socials?.youtube || "",
        tags: project.tags || [],
        isHomepage: !!project.isHomepage,
        isFeatured: !!project.isFeatured,
        order: project.order ?? null,
        sections: Array.isArray(project.sections)
          ? project.sections.map((s) => ({
              title: s.title || "",
              description: s.description || "",
            }))
          : [],
      });

      // show existing images in the Upload as "done" previews (won't be re-uploaded)
      setLogoFiles(
        project.logo
          ? [
              {
                uid: "logo-1",
                name: project.logo.split("/").pop() || "logo",
                status: "done",
                url: project.logo,
              },
            ]
          : []
      );
      setBannerFiles(
        project.bannerImage
          ? [
              {
                uid: "banner-1",
                name: project.bannerImage.split("/").pop() || "banner",
                status: "done",
                url: project.bannerImage,
              },
            ]
          : []
      );
      setProgramLogoFiles(
        project?.program?.logo
          ? [
              {
                uid: "proglogo-1",
                name: project.program.logo.split("/").pop() || "program-logo",
                status: "done",
                url: project.program.logo,
              },
            ]
          : []
      );
      setLogoPct(0);
      setBannerPct(0);
      setProgramLogoPct(0);
    } else {
      form.resetFields();
      setLogoFiles([]);
      setBannerFiles([]);
      setProgramLogoFiles([]);
      setLogoPct(0);
      setBannerPct(0);
      setProgramLogoPct(0);
    }
  }, [open, project, form]);

  const submit = async (values) => {
    setSaving(true);
    setLogoPct(0);
    setBannerPct(0);
    setProgramLogoPct(0);

    try {
      // Upload only if a new file (with originFileObj) is selected; otherwise keep/accept URL.
      let logoUrl = (values.logo || "").trim();
      if (logoFiles[0]?.originFileObj) {
        logoUrl = await uploadOne(rawFile(logoFiles[0]), setLogoPct);
        setLogoPct(100);
      } else if (!logoUrl && logoFiles[0]?.url) {
        logoUrl = logoFiles[0].url; // existing preview URL
      }

      let bannerUrl = (values.bannerImage || "").trim();
      if (bannerFiles[0]?.originFileObj) {
        bannerUrl = await uploadOne(rawFile(bannerFiles[0]), setBannerPct);
        setBannerPct(100);
      } else if (!bannerUrl && bannerFiles[0]?.url) {
        bannerUrl = bannerFiles[0].url;
      }

      let programLogoUrl = (values.program_logo || "").trim();
      if (programLogoFiles[0]?.originFileObj) {
        programLogoUrl = await uploadOne(
          rawFile(programLogoFiles[0]),
          setProgramLogoPct
        );
        setProgramLogoPct(100);
      } else if (!programLogoUrl && programLogoFiles[0]?.url) {
        programLogoUrl = programLogoFiles[0].url;
      }

      // build payload to match backend
      const payload = {
        name: values.name,
        slug: values.slug || undefined,
        programName: values.programName || "",
        type: values.type || "",
        referenceNumber: values.referenceNumber || "",
        startDate: values.startDate ? values.startDate.toDate() : undefined,
        endDate: values.endDate ? values.endDate.toDate() : undefined,
        logo: logoUrl || "",
        bannerImage: bannerUrl || "",
        overviewHtml: values.overviewHtml || "",
        program: {
          name: values.program_name || "",
          logo: programLogoUrl || "",
          description: values.program_description || "",
        },
        socials: {
          website: values.website || "",
          facebook: values.facebook || "",
          instagram: values.instagram || "",
          x: values.x || "",
          linkedin: values.linkedin || "",
          youtube: values.youtube || "",
        },
        tags: values.tags || [],
        isHomepage: !!values.isHomepage,
        isFeatured: !!values.isFeatured,
        order: values.order ?? undefined,
        sections: Array.isArray(values.sections)
          ? values.sections.map((s) => ({
              title: s?.title || "",
              description: s?.description || "",
            }))
          : [],
      };

      let res;
      if (project?._id) {
        res = await axios.patch(routes.capacityAdminItem(project._id), payload);
      } else {
        res = await axios.post(routes.capacityAdminList, payload);
      }
      const updated = res?.data?.project || res?.data;
      message.success("Saved");
      onSaved && onSaved(updated);
      onClose && onClose();
    } catch (e) {
      message.error(e?.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const onPreview = (file) =>
    setPreview({
      open: true,
      src: file.url || file.thumbUrl,
      title: file.name || "Preview",
    });

  return (
    <Modal
      open={open}
      title={project ? `Edit: ${project.name}` : "Add Project"}
      onCancel={onClose}
      onOk={() => form.submit()}
      width={900}
      confirmLoading={saving}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={submit}>
        <Flex gap={12}>
          <Form.Item
            name="name"
            label="Project Name"
            rules={[{ required: true }]}
            style={{ flex: 1 }}
          >
            <Input placeholder="Project Name" />
          </Form.Item>
          <Form.Item name="type" label="Type" style={{ flex: 1 }}>
            <Input placeholder="Type" />
          </Form.Item>
          <Form.Item
            name="referenceNumber"
            label="Reference Number"
            style={{ flex: 1 }}
          >
            <Input />
          </Form.Item>
        </Flex>

        <Flex gap={12}>
          <Form.Item name="startDate" label="Start Date" style={{ flex: 1 }}>
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="endDate" label="End Date" style={{ flex: 1 }}>
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
        </Flex>

        {/* Logo + Banner with URL or Upload */}
        <Space style={{ marginBottom: "1rem" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            Logo Image
            <Upload
              listType="picture-card"
              fileList={logoFiles}
              beforeUpload={() => false} // upload happens on Save
              onChange={({ fileList }) => setLogoFiles(fileList.slice(-1))}
              onPreview={onPreview}
              maxCount={1}
              accept="image/*"
            >
              {logoFiles.length >= 1 ? null : (
                <div style={{ width: 110 }}>
                  <UploadOutlined />{" "}
                  <div style={{ marginTop: 4 }}>Upload Logo</div>
                </div>
              )}
            </Upload>
            {saving && logoFiles[0]?.originFileObj && (
              <div style={{ width: 160, alignSelf: "center" }}>
                <div style={{ fontSize: 12, opacity: 0.7 }}>Logo upload</div>
                <Progress percent={logoPct} size="small" />
              </div>
            )}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            Banner Image
            <Upload
              listType="picture-card"
              fileList={bannerFiles}
              beforeUpload={() => false}
              onChange={({ fileList }) => setBannerFiles(fileList.slice(-1))}
              onPreview={onPreview}
              maxCount={1}
              accept="image/*"
            >
              {bannerFiles.length >= 1 ? null : (
                <div style={{ width: 160 }}>
                  <UploadOutlined />{" "}
                  <div style={{ marginTop: 4 }}>Upload Banner</div>
                </div>
              )}
            </Upload>
            {saving && bannerFiles[0]?.originFileObj && (
              <div style={{ width: 160, alignSelf: "center" }}>
                <div style={{ fontSize: 12, opacity: 0.7 }}>Banner upload</div>
                <Progress percent={bannerPct} size="small" />
              </div>
            )}
          </div>
        </Space>

        <Form.Item name="overviewHtml" label="Overview / Description">
          <RichText
            value={form.getFieldValue("overviewHtml")}
            onChange={(html) => form.setFieldsValue({ overviewHtml: html })}
          />
        </Form.Item>

        <Divider />

        {/* Program info + Program Logo */}
        <Flex justify="space-between" gap={12} wrap>
          <Form.Item
            name="program_name"
            label="Program Name"
            style={{ flex: 1 }}
          >
            <Input placeholder="e.g., Erasmus+" />
          </Form.Item>

          <Upload
            listType="picture-card"
            fileList={programLogoFiles}
            beforeUpload={() => false}
            onChange={({ fileList }) => setProgramLogoFiles(fileList.slice(-1))}
            onPreview={onPreview}
            maxCount={1}
            accept="image/*"
          >
            {programLogoFiles.length >= 1 ? null : (
              <div style={{ width: 160 }}>
                <UploadOutlined />{" "}
                <div style={{ marginTop: 4 }}>Upload Program Logo</div>
              </div>
            )}
          </Upload>
          {saving && programLogoFiles[0]?.originFileObj && (
            <div style={{ width: 180, alignSelf: "center" }}>
              <div style={{ fontSize: 12, opacity: 0.7 }}>
                Program logo upload
              </div>
              <Progress percent={programLogoPct} size="small" />
            </div>
          )}
        </Flex>

        <Form.Item
          name="program_description"
          label="Program Description"
          style={{ flex: 1 }}
        >
          <RichText
            value={form.getFieldValue("program_description")}
            onChange={(html) =>
              form.setFieldsValue({ program_description: html })
            }
          />
        </Form.Item>

        <Flex gap={12} wrap>
          <Form.Item name="website" label="Website">
            <Input placeholder="https://…" />
          </Form.Item>
          <Form.Item name="facebook" label="Facebook">
            <Input />
          </Form.Item>
          <Form.Item name="instagram" label="Instagram">
            <Input />
          </Form.Item>
          <Form.Item name="x" label="X (Twitter)">
            <Input />
          </Form.Item>
          <Form.Item name="linkedin" label="LinkedIn">
            <Input />
          </Form.Item>
          <Form.Item name="youtube" label="YouTube">
            <Input />
          </Form.Item>
        </Flex>

        <Flex gap={12}>
          <Form.Item name="tags" label="Tags" style={{ flex: 1 }}>
            <Select
              mode="tags"
              placeholder="Type and press Enter to add tags"
            />
          </Form.Item>

          <Space align="center" style={{ gap: 16, flex: 1 }} wrap>
            <Form.Item
              name="isHomepage"
              label="Homepage"
              valuePropName="checked"
              style={{ flex: 1 }}
            >
              <Switch />
            </Form.Item>
            <Form.Item
              name="isFeatured"
              label="Featured"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </Space>
        </Flex>

        <Divider orientation="left">Sections</Divider>

        <Form.List name="sections">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...rest }) => {
                const descValue =
                  form.getFieldValue(["sections", name, "description"]) || "";
                return (
                  <Card
                    key={key}
                    size="small"
                    style={{ marginBottom: 12, background: "#fafafa" }}
                    title={
                      <Form.Item
                        {...rest}
                        name={[name, "title"]}
                        rules={[
                          {
                            required: true,
                            message: "Section title is required",
                          },
                        ]}
                        style={{ marginBottom: 0, width: "100%" }}
                      >
                        <Input placeholder="Section title (e.g., Objectives, Impact, Methodology…)" />
                      </Form.Item>
                    }
                    extra={
                      <Button
                        danger
                        type="text"
                        icon={<DeleteOutlined />}
                        onClick={() => remove(name)}
                      >
                        Remove
                      </Button>
                    }
                  >
                    <Form.Item
                      {...rest}
                      name={[name, "description"]}
                      label="Description"
                    >
                      <RichText
                        value={descValue}
                        onChange={(html) =>
                          form.setFieldsValue({
                            sections: (
                              form.getFieldValue("sections") || []
                            ).map((s, idx) =>
                              idx === name ? { ...s, description: html } : s
                            ),
                          })
                        }
                      />
                    </Form.Item>
                  </Card>
                );
              })}

              <Button
                type="dashed"
                block
                icon={<PlusOutlined />}
                onClick={() => add({ title: "", description: "" })}
              >
                Add section
              </Button>
            </>
          )}
        </Form.List>
      </Form>

      <Modal
        open={preview.open}
        footer={null}
        title={preview.title}
        onCancel={() => setPreview((p) => ({ ...p, open: false }))}
      >
        <img alt="preview" style={{ width: "100%" }} src={preview.src} />
      </Modal>
    </Modal>
  );
}
